/**
 * Structured, DSGVO-aware application logger.
 *
 * Why not just `console.log`?
 *  - Log levels stay consistent across the codebase.
 *  - In production we suppress `debug`/`info` to keep the console clean.
 *  - `error()` automatically forwards to Sentry **if it is initialised**,
 *    without requiring every call site to import Sentry directly.
 *  - A single chokepoint makes it easy to add a future log shipper.
 *
 * **Important — DSGVO:** Never pass raw patient data to the logger. Pass
 * IDs and short categorical descriptions only. The Sentry integration
 * also strips PII from breadcrumbs (see `lib/sentry.ts`).
 */

import { env } from './env';

type Level = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_ORDER: Record<Level, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

const MIN_LEVEL: Level = env.isProd ? 'warn' : 'debug';

function shouldLog(level: Level): boolean {
  return LEVEL_ORDER[level] >= LEVEL_ORDER[MIN_LEVEL];
}

function format(scope: string, message: string): string {
  return `[${scope}] ${message}`;
}

/**
 * Forward an error to Sentry if it has been initialised. The dynamic import
 * keeps Sentry out of the critical-path bundle for users who do not need it
 * (e.g. local dev without VITE_SENTRY_DSN).
 */
function reportToSentry(error: unknown, context?: Record<string, unknown>): void {
  if (!env.sentryDsn) return;
  // Avoid pulling Sentry into the boot path; load it lazily on first error.
  void import('@sentry/react').then((Sentry) => {
    if (error instanceof Error) {
      Sentry.captureException(error, context ? { extra: context } : undefined);
    } else {
      Sentry.captureMessage(String(error), {
        level: 'error',
        extra: context,
      });
    }
  });
}

export interface Logger {
  debug: (message: string, data?: unknown) => void;
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
  error: (message: string, errorOrData?: unknown, extra?: Record<string, unknown>) => void;
  child: (subScope: string) => Logger;
}

/**
 * Create a logger bound to a scope (component / module name).
 *
 * @example
 *   const log = createLogger('PatientList');
 *   log.info('loaded', { count: 42 });
 *   log.error('failed to delete', err, { patientId });
 */
export function createLogger(scope: string): Logger {
  return {
    debug(message, data) {
      if (!shouldLog('debug')) return;
      console.debug(format(scope, message), data ?? '');
    },
    info(message, data) {
      if (!shouldLog('info')) return;
      console.info(format(scope, message), data ?? '');
    },
    warn(message, data) {
      if (!shouldLog('warn')) return;
      console.warn(format(scope, message), data ?? '');
    },
    error(message, errorOrData, extra) {
      console.error(format(scope, message), errorOrData ?? '', extra ?? '');
      if (errorOrData != null) {
        reportToSentry(errorOrData, { scope, message, ...(extra ?? {}) });
      }
    },
    child(subScope) {
      return createLogger(`${scope}/${subScope}`);
    },
  };
}

/** Default logger for ad-hoc use. Prefer `createLogger('YourScope')`. */
export const logger = createLogger('app');
